import { useState, useEffect, useCallback, useRef } from "react";

const STORAGE_KEY_PREFIX = "striply_layout_";
const DEBOUNCE_MS = 500;

/**
 * Custom hook for persisting layout state to localStorage
 * @param {string} layoutId - Unique identifier for the layout
 * @param {Function} createInitialCells - Function to create fresh cells if no saved state
 * @returns {Object} - { cells, setCells, clearSavedState, hasSavedState }
 */
export const useLayoutStorage = (layoutId, createInitialCells) => {
  const [cells, setCells] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const saveTimeoutRef = useRef(null);
  const storageKey = `${STORAGE_KEY_PREFIX}${layoutId || "default"}`;

  // Load from localStorage on mount
  useEffect(() => {
    if (isInitialized) return;

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed.cells) && parsed.cells.length > 0) {
          setCells(parsed.cells);
          setIsInitialized(true);
          return;
        }
      }
    } catch (e) {
      console.warn("Failed to load saved layout:", e);
    }

    // No saved state, create initial cells
    const initial = createInitialCells();
    setCells(initial);
    setIsInitialized(true);
  }, [storageKey, createInitialCells, isInitialized]);

  // Save to localStorage when cells change (debounced)
  useEffect(() => {
    if (!isInitialized || cells.length === 0) return;

    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce the save
    saveTimeoutRef.current = setTimeout(() => {
      try {
        // Filter out non-serializable items (like ad slots) and prepare for storage
        const serializableCells = cells.map((cell) => ({
          ...cell,
          items: (cell.items || []).filter(
            (item) => item.type !== "ad" && item.type !== "tipCard"
          ),
        }));

        const data = {
          cells: serializableCells,
          savedAt: Date.now(),
          version: 1,
        };

        localStorage.setItem(storageKey, JSON.stringify(data));
      } catch (e) {
        console.warn("Failed to save layout:", e);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [cells, storageKey, isInitialized]);

  const clearSavedState = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {
      console.warn("Failed to clear saved layout:", e);
    }
  }, [storageKey]);

  const hasSavedState = useCallback(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed.cells) && parsed.cells.length > 0;
      }
    } catch (e) {
      // Ignore
    }
    return false;
  }, [storageKey]);

  return {
    cells,
    setCells,
    clearSavedState,
    hasSavedState,
    isInitialized,
  };
};

/**
 * Convert a File/Blob to base64 for localStorage storage
 * @param {File|Blob} file - The file to convert
 * @returns {Promise<string>} - Base64 data URL
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Get all saved layout IDs from localStorage
 * @returns {string[]} - Array of layout IDs
 */
export const getSavedLayoutIds = () => {
  const ids = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        ids.push(key.replace(STORAGE_KEY_PREFIX, ""));
      }
    }
  } catch (e) {
    console.warn("Failed to enumerate saved layouts:", e);
  }
  return ids;
};
